import {RefObject, useCallback, useState} from "react";
import {
  ChatModelRequest,
  ChatModelResponse,
  ChatWithDanaModel,
  ChatWithDanaRequest,
  DeleteChatRequest,
  SendMessageRequest,
} from "@/app/api/chatWithDana";
import {SearchParamsKey, useUrlParams} from "@/app/utils/search_params";

export interface MedicinesState {
  hasError: string | null;
  hasLoading: boolean;
  hasMoreLoading: boolean;
  isAll: boolean;
  isNewChat: boolean;
  showDeleteButton: boolean;
  chats: ChatWithDanaModel[];
  currentChat?: number;
  page: number;
  showMenu: boolean;
  hasSending: boolean;
  chat: ChatModelResponse[];
  files: File[];
}

const useChatWithDana = () => {
  const {setUrlParam, getUrlParam, deleteUrlParam} = useUrlParams();
  const [state, setState] = useState<MedicinesState>({
    hasError: null,
    hasLoading: true,
    hasMoreLoading: false,
    isAll: false,
    isNewChat: false,
    showDeleteButton: false,
    chats: [],
    currentChat: undefined,
    page: 0,
    showMenu: true,
    hasSending: false,
    chat: [],
    files: [],
  });

  const updateState = useCallback((updates: Partial<MedicinesState>) => {
    setState(prev => ({...prev, ...updates}));
  }, []);

  const init = useCallback(async () => {
    const chatIdParam = getUrlParam(SearchParamsKey.chatWithDanaId);
    try {
      const chats = await ChatWithDanaRequest.getChatsWithDana();
      chats.forEach((chat) => {
        chat.lastUserRequest.length <= 0 && (chat.lastUserRequest = "Image Analysis");
      })
      if (chatIdParam) {
        const selectedChat = chats.find(
          (chat) => chat.id.toString() === chatIdParam
        );
        if (selectedChat) {
          setCurrentChat(selectedChat.id);
        }
      }
      updateState({chats, hasLoading: false});
    } catch (error: any) {
      updateState({hasError: error.message || "Error fetching chats", hasLoading: false});
    }
  }, [getUrlParam, updateState]);

  const getChat = useCallback(
    async (id?: number, page?: number) => {
      const chatId = id !== undefined ? id : state.currentChat;
      if (chatId === undefined || state.isAll || state.hasMoreLoading) return;

      const currentPage = page !== undefined ? page : state.page;
      updateState({hasMoreLoading: true});
      const data: ChatModelRequest = {chatId, page: currentPage};

      try {
        const chatData = await ChatWithDanaRequest.getChat(data);
        if (chatData && chatData.length > 0) {
          updateState({
            chat: [...state.chat, ...chatData],
            page: state.page + 1,
            hasMoreLoading: false,
          });
        } else {
          updateState({isAll: true, hasMoreLoading: false});
        }
      } catch (error) {
        updateState({hasMoreLoading: false});
      }
    },
    [state.currentChat, state.page, state.isAll, state.hasMoreLoading, state.chat, updateState]
  );

  const setCurrentChat = useCallback(
    (id: number) => {
      updateState({
        currentChat: id,
        chat: [],
        page: 0,
        hasMoreLoading: false,
        isAll: false,
      });
      setUrlParam(SearchParamsKey.chatWithDanaId, id.toString());
      getChat(id, 0);
    },
    [setUrlParam, getChat, updateState]
  );

  const toggleMenu = useCallback(() => {
    updateState({showMenu: !state.showMenu});
  }, [state.showMenu, updateState]);

  const toggleShowDelete = useCallback(() => {

    updateState({showDeleteButton: !state.showDeleteButton});
  }, [state.showDeleteButton, updateState]);

  const deleteChat = useCallback(async () => {
    if (state.currentChat === undefined) return;
    const data: DeleteChatRequest = {chatId: state.currentChat};
    try {
      await ChatWithDanaRequest.deleteChat(data);
      updateState({
        currentChat: undefined,
        chat: [],
        page: 0,
        hasMoreLoading: false,
        isAll: false,
        showDeleteButton: false,
      });
      await init();
      deleteUrlParam(SearchParamsKey.chatWithDanaId);
    } catch (error) {
      console.error(error);
    }
  }, [state.currentChat, init, updateState]);

  const prepareFiles = useCallback((files: File[]): FormData => {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    return formData;
  }, []);

  const sendMessage = useCallback(
    async (ref: RefObject<HTMLTextAreaElement>) => {
      // Если уже идёт отправка, ничего не делаем
      if (state.hasSending) return;

      updateState({hasSending: true});
      const text = ref.current?.value.trim() ?? "";
      const newItem: ChatModelResponse = {
        userRequest: text,
        modelResponse: "",
        filesLinks: [],
      };

      if (ref.current) {
        ref.current.value = "";
      }

      let uploadedFiles: string[] = [];
      if (state.files && state.files.length > 0) {
        const formData = prepareFiles(state.files);
        try {
          uploadedFiles = await ChatWithDanaRequest.sendFiles(formData);
          newItem.filesLinks = uploadedFiles;
        } catch (error) {
          console.error("Ошибка загрузки файлов", error);
        }
        updateState({files: []});
      }

      try {
        if (state.currentChat === undefined || state.currentChat === 1) {
          // Создание нового чата
          const newChat = await ChatWithDanaRequest.createChat();
          const data: SendMessageRequest = {
            chatId: newChat.id,
            imagesUrlsList: uploadedFiles.join(","),
            userInput: newItem.userRequest,
            userLanguage: "en",
          };
          newChat.lastUserRequest = text;
          const newChats = [newChat, ...state.chats.slice(1)];
          updateState({
            chat: [newItem, ...state.chat],
            isNewChat: true,
            chats: newChats,
            currentChat: newChat.id,
          });
          const msg = await ChatWithDanaRequest.sendMessage(data);
          newItem.modelResponse = msg.response;
          newItem.filesLinks = uploadedFiles;
          updateState({
            chat: [newItem, ...state.chat],
            isNewChat: false,
          });
          setCurrentChat(newChat.id);
          await init();
        } else {
          // Отправка сообщения в существующий чат
          const data: SendMessageRequest = {
            chatId: state.currentChat!,
            imagesUrlsList: uploadedFiles.join(","),
            userInput: newItem.userRequest,
            userLanguage: "en",
          };
          updateState({chat: [newItem, ...state.chat]});
          const msg = await ChatWithDanaRequest.sendMessage(data);
          newItem.modelResponse = msg.response;
          newItem.filesLinks = uploadedFiles;
          updateState({chat: [newItem, ...state.chat]});
          await init();
        }
      } catch (error) {
        console.error(error);
      } finally {
        updateState({hasSending: false});
      }
    },
    [
      state.hasSending,
      state.files,
      state.currentChat,
      state.chats,
      state.chat,
      prepareFiles,
      init,
      updateState,
      setCurrentChat,
    ]
  );

  const addFiles = useCallback(
    async (files: File[]) => {
      updateState({files});
    },
    [updateState]
  );

  const createNewChat = useCallback(() => {
    updateState({
      currentChat: undefined,
      chat: [],
      page: 0,
      hasMoreLoading: false,
      isAll: false,
      showDeleteButton: false,
    });
  }, [updateState]);

  return {
    state,
    updateState,
    init,
    getChat,
    setCurrentChat,
    toggleMenu,
    toggleShowDelete,
    deleteChat,
    sendMessage,
    createNewChat,
    addFiles,
  };
};

export default useChatWithDana;